import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, Package, Mail, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_POLL_ATTEMPTS = 5;
const POLL_INTERVAL_MS = 2000;

export const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) return undefined;

    let cancelled = false;
    let attempts = 0;
    let timer = null;

    const poll = async () => {
      if (cancelled) return;
      if (attempts >= MAX_POLL_ATTEMPTS) {
        setPaymentStatus('timeout');
        return;
      }
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === 'paid') {
          const purchaseValue = (data.amount_total || 8999) / 100;
          const orderId = data.session_id || sessionId;
          // De-dupe: only fire once per session_id even if user refreshes
          const firedKey = `cfy_purchase_fired_${orderId}`;
          if (!window.sessionStorage.getItem(firedKey)) {
            if (window.fbq) {
              window.fbq('track', 'Purchase', {
                value: purchaseValue,
                currency: 'USD',
                content_ids: ['CFY-BUNDLE-DUO-001'],
                content_type: 'product',
                content_name: 'Snatched Duo Bundle + Free Boyshorts'
              });
            }
            if (window.gtag) {
              window.gtag('event', 'purchase', {
                transaction_id: orderId,
                value: purchaseValue,
                currency: 'USD',
                items: [{
                  item_id: 'CFY-BUNDLE-DUO-001',
                  item_name: 'Snatched Duo Bundle + Free Boyshorts',
                  price: purchaseValue,
                  quantity: 1
                }]
              });
            }
            window.sessionStorage.setItem(firedKey, '1');
          }
          setPaymentStatus('success');
          setPaymentDetails(data);
          return;
        }
        if (data.status === 'expired') {
          setPaymentStatus('expired');
          return;
        }
        attempts += 1;
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        if (!cancelled) setPaymentStatus('error');
      }
    };

    poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId]);

  if (!sessionId) return <Navigate to="/" replace />;

  if (paymentStatus === 'checking') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-rose-50/30 to-white flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#7d1f3d] mx-auto mb-6"></div>
            <h2 className="text-2xl font-bold text-[#7d1f3d] mb-2">
              Processing Your Payment...
            </h2>
            <p className="text-gray-600">
              Please wait while we confirm your order.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-rose-50/30 to-white py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <Card className="border-2 border-green-200 shadow-2xl">
            <CardContent className="p-12">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-12 h-12 text-green-600" />
                </div>
                <h1 className="text-4xl font-bold text-[#7d1f3d] mb-4">
                  Order Confirmed!
                </h1>
                <p className="text-xl text-gray-600">
                  Thank you for your purchase
                </p>
              </div>

              <div className="bg-rose-50/50 rounded-xl p-6 mb-8">
                <h3 className="font-bold text-[#7d1f3d] mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Order Details
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Product:</strong> Snatched Duo Bundle + Free Boyshorts</p>
                  <p><strong>Amount Paid:</strong> ${paymentDetails?.amount_total ? (paymentDetails.amount_total / 100).toFixed(2) : '89.99'}</p>
                  <p><strong>Order ID:</strong> {sessionId?.substring(0, 20)}...</p>
                </div>
              </div>

              <div className="bg-blue-50 rounded-xl p-6 mb-8">
                <h3 className="font-bold text-[#7d1f3d] mb-3 flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  What&apos;s Next?
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>You&apos;ll receive an order confirmation email shortly</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>We&apos;ll send you shipping updates as your order is processed</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Expect delivery within 3-5 business days</span>
                  </li>
                </ul>
              </div>

              <div className="text-center">
                <Button
                  onClick={() => navigate('/')}
                  className="bg-[#7d1f3d] hover:bg-[#5d1729] text-white px-8 py-6 text-lg"
                >
                  Continue Shopping
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-gray-600 mt-8">
            Questions? Contact us at <a href="mailto:info@curveeforyou.com" className="text-[#7d1f3d] hover:underline">info@curveeforyou.com</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-rose-50/30 to-white flex items-center justify-center">
      <Card className="max-w-md w-full mx-4">
        <CardContent className="p-8 text-center">
          <h2 className="text-2xl font-bold text-[#7d1f3d] mb-4">
            {paymentStatus === 'timeout' ? 'Payment Status Check Timed Out' : 
             paymentStatus === 'expired' ? 'Payment Session Expired' : 
             'Payment Error'}
          </h2>
          <p className="text-gray-600 mb-6">
            {paymentStatus === 'timeout' 
              ? 'Please check your email for confirmation or contact support.' 
              : 'There was an issue processing your payment. Please try again.'}
          </p>
          <Button
            onClick={() => navigate('/')}
            className="bg-[#7d1f3d] hover:bg-[#5d1729] text-white"
          >
            Return to Home
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
